/**
 * Vendor prefixes used in canonical pathology names (stored in DB + sent to
 * the AI backend) that we don't want to surface in the UI.
 *
 * The canonical `pathology_name` is kept untouched everywhere it leaves the
 * client (API calls, form data, export metadata) — we only strip the prefix
 * at render time via this helper.
 */
const VENDOR_PREFIXES = ['LDBIO ']

export function pathologyDisplayName(name: string | null | undefined): string {
  if (!name) return ''
  let out = name
  // Strip the vendor prefix (e.g. "LDBIO ").
  const upper = out.toUpperCase()
  for (const prefix of VENDOR_PREFIXES) {
    if (upper.startsWith(prefix)) {
      out = out.slice(prefix.length)
      break
    }
  }
  // Strip a trailing "(Western Blot)" descriptor — the format is implied by the
  // module, so "LDBIO TOXOCARA WB IgG (Western Blot)" surfaces as "TOXOCARA WB IgG".
  out = out.replace(/\s*\(\s*western\s*blot\s*\)\s*$/i, '')
  return out.trim()
}

/**
 * Strip the series marker and the scaffolding from a pathology name, keeping
 * it readable. Shared by the grouping key and the label it is shown under.
 *
 * The leading "z" a Dynablot sheet prints on some labels goes: it marks a
 * different SERIES of a test, not a different test — "zAspergillus IgG" is
 * Aspergillus, and so is "ZAspergillusIgG". The series distinction still
 * exists where it matters, in the plate's own filters (see canonical_self_test
 * in careflai-ai-1); what it must not do is split one search into two rows of
 * a ranking of searches.
 *
 * "WB" and "CONFIRMATION" say how a test is run, not which test it is, and
 * drop as whole tokens — so "CIP-WB" keeps its own identity. "ES" goes with
 * them: it names the antigen of the catalogue's only Trichinella test, so it
 * can tell no two tests apart, while keeping it would split the catalogue's
 * "TRICHINELLA ES WB IgG" from the "Trichinella IgG" a sheet prints.
 */
const SCAFFOLDING = new Set(['WB', 'CONFIRMATION', 'LDBIO', 'ES'])

function stripPathologyScaffolding(name: string): string {
  const display = pathologyDisplayName(name).replace(/^z(?=[A-Za-z])/i, '')
  // A slug ("ldbio-aspergillus-igg") only when the name has no spaces of its
  // own — that is what tells it from a written name whose hyphen belongs.
  const words = /\s/.test(display) ? display : display.replace(/-+/g, ' ')
  return words
    .split(/\s+/)
    .filter((token) => !SCAFFOLDING.has(token.toUpperCase()))
    .join(' ')
    .trim()
}

/**
 * Key under which every spelling of the SAME test is counted together.
 *
 * A name reaches us written three ways over. The catalogue spells it out
 * ("LDBIO TOXO II IgG CONFIRMATION"); a declared pathology may be a slug
 * ("ldbio-aspergillus-igg"); and what a Dynablot sheet prints is READ BY OCR,
 * which loses spaces as it pleases — staging holds "Schisto II IgG",
 * "SchistoII IgG" and "SchistoIIIgG" side by side, each ranked as its own
 * test. So spacing cannot be part of the identity: the key keeps letters and
 * digits only, and the format markers go with it, which is why they are
 * dropped as tokens BEFORE the separators are.
 *
 * What survives is what names the test — "SCHISTOIIIGG" — so the three
 * spellings land on one row. No pathology in the catalogue contains the
 * letters "wb", so removing that marker from the squeezed key cannot damage a
 * real name, while "CIP-WB" stays apart on the rest of its name.
 */
export function pathologyGroupKey(name: string | null | undefined): string {
  if (!name) return ''
  return stripPathologyScaffolding(name)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .replace(/CONFIRMATION/g, '')
    .replace(/WB/g, '')
}

/**
 * The label a group is shown under, chosen between the spellings it holds.
 *
 * Preference goes to the most separated one — an OCR that kept its spaces read
 * the sheet better — then to the one that is not shouting, then to the
 * shortest. "Schisto II IgG" therefore wins over "SchistoII IgG",
 * "SCHISTO II IgG" and "SchistoIIIgG".
 */
export function pathologyGroupLabel(name: string | null | undefined): string {
  return name ? stripPathologyScaffolding(name) : ''
}

export function preferredPathologyLabel(a: string, b: string): string {
  const spaces = (v: string) => (v.match(/\s/g) ?? []).length
  if (spaces(a) !== spaces(b)) return spaces(a) > spaces(b) ? a : b
  const shouting = (v: string) => (v === v.toUpperCase() ? 1 : 0)
  if (shouting(a) !== shouting(b)) return shouting(a) < shouting(b) ? a : b
  return a.length <= b.length ? a : b
}

/**
 * A *complete* strip identifier is a letter followed by 1–3 digits (e.g. "K10").
 * A bare letter prefix ("K", suggested by OCR when the number couldn't be read)
 * is NOT complete — the biologist still has to type the number. Exports gate on
 * this so a report never ships a half-identified strip.
 */
export function isCompleteStripId(stripId: string | null | undefined): boolean {
  return /^[A-Za-z]\d{1,3}$/.test((stripId ?? '').trim())
}

/**
 * A strip id being *typed* is acceptable when it's a single letter optionally
 * followed by up to 3 digits — "K" (draft, number still to add) or "K10"
 * (complete). This rejects malformed entries the biologist should never save:
 * digit-only ids ("123"), symbols ("T+"), or multi-letter prefixes ("KK"). Used
 * to validate the editable id field at entry time, before it ever reaches the DB.
 * An empty string is allowed (it clears the id).
 */
export function isValidStripIdDraft(stripId: string): boolean {
  const v = stripId.trim()
  return v === '' || /^[A-Za-z]\d{0,3}$/.test(v)
}

/**
 * Human label for a strip: its recognised/edited identifier when present,
 * otherwise the positional fallback "Blot N". Used everywhere a strip is named
 * (gallery, modal headings, exports) so the label stays consistent.
 */
export function blotLabel(blot: { id: number; strip_id?: string | null }): string {
  const sid = (blot?.strip_id ?? '').trim()
  return sid.length > 0 ? sid : `Blot ${blot?.id}`
}
